import { cfpBaseUrl } from "../network/urls";
import {
  clearAdminCompanySelection,
  SELECTED_ADMIN_COMPANY_STORAGE_KEY,
  LAST_ADMIN_COMPANY_PREF_KEY,
} from "./adminCompany";

export const TAB_SESSION_MARKER = "eops:tabSessionMarker";
export const TAB_CLOSE_LOGOUT_FLAG = "eops:tabCloseLogoutApplied";
export const PERSIST_ROOT_KEY = "persist:root";
export const EXPECT_RELOAD_KEY = "eops:expectReload";
export const PENDING_TAB_CLOSE_LOGOUT_KEY = "eops:pendingCloseLogout";
export const BILL_TAB_AUTH_BRIDGE_KEY = "eops:billTabAuthBridge";
export const BILL_TAB_AUTH_BRIDGE_AT_KEY = "eops:billTabAuthBridgeAt";
/** Bridge is only valid for a short window after Bill click */
const BILL_TAB_AUTH_BRIDGE_TTL_MS = 30_000;

/** @deprecated legacy key cleared on bootstrap */
export const TIME_ORIGIN_KEY = "eops:timeOrigin";

export const isReloadNavigation = () => {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav?.type === "reload") return true;
    if (typeof performance.navigation?.type === "number") {
      return performance.navigation.type === 1;
    }
  } catch {
    // ignore
  }
  return false;
};

export const readPersistedToken = () => {
  try {
    const raw = sessionStorage.getItem(PERSIST_ROOT_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    const auth = JSON.parse(parsed.auth || "{}");
    return auth.token || "";
  } catch {
    return "";
  }
};

export const callLogoutAPIKeepalive = (token) => {
  if (!token) return;

  fetch(`${cfpBaseUrl}customerFaciliationPortal/logOut`, {
    method: "PUT",
    headers: { Authorization: token },
    keepalive: true,
  }).catch(() => {});
};

export const migrateLegacyAuthStorage = () => {
  try {
    localStorage.removeItem(PERSIST_ROOT_KEY);
    localStorage.removeItem("userId");
    localStorage.removeItem("user-token");
    localStorage.removeItem("eops:pendingLogout");
    localStorage.removeItem("eops:openTabs");
    localStorage.removeItem("appTabs");
    localStorage.removeItem("eops:sessionHeartbeat");
    localStorage.removeItem("eops:reloading");
    localStorage.removeItem("eops:tabClosed");
  } catch {
    // ignore
  }
};

export const clearClientAuthStorage = () => {
  sessionStorage.removeItem(PERSIST_ROOT_KEY);
  sessionStorage.removeItem(TAB_SESSION_MARKER);
  sessionStorage.removeItem(TIME_ORIGIN_KEY);
  sessionStorage.removeItem(EXPECT_RELOAD_KEY);
  sessionStorage.removeItem("eops:liveToken");
  localStorage.removeItem(PERSIST_ROOT_KEY);
  localStorage.removeItem(BILL_TAB_AUTH_BRIDGE_KEY);
  localStorage.removeItem(BILL_TAB_AUTH_BRIDGE_AT_KEY);
  localStorage.removeItem(PENDING_TAB_CLOSE_LOGOUT_KEY);
  localStorage.removeItem("userId");
  localStorage.removeItem("user-token");
  localStorage.removeItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY);
  localStorage.removeItem(LAST_ADMIN_COMPANY_PREF_KEY);
  localStorage.removeItem("eops:pendingCloseLogout");
  localStorage.removeItem("eops:tabClosed");
  clearAdminCompanySelection();
};

const consumeBillTabAuthBridge = () => {
  try {
    const isBillTab =
      typeof window !== "undefined" &&
      (String(window.location?.pathname || "").includes("advance-payment-bill") ||
        String(window.location?.pathname || "").includes("mrc-bill"));
    if (!isBillTab) return false;

    const bridged = localStorage.getItem(BILL_TAB_AUTH_BRIDGE_KEY);
    const bridgedAt = Number(localStorage.getItem(BILL_TAB_AUTH_BRIDGE_AT_KEY) || 0);
    localStorage.removeItem(BILL_TAB_AUTH_BRIDGE_KEY);
    localStorage.removeItem(BILL_TAB_AUTH_BRIDGE_AT_KEY);

    if (!bridged) return false;
    if (!bridgedAt || Date.now() - bridgedAt > BILL_TAB_AUTH_BRIDGE_TTL_MS) {
      return false;
    }

    sessionStorage.setItem(PERSIST_ROOT_KEY, bridged);
    sessionStorage.setItem(TAB_SESSION_MARKER, String(Date.now()));
    localStorage.removeItem(PENDING_TAB_CLOSE_LOGOUT_KEY);
    return true;
  } catch {
    return false;
  }
};

/**
 * Same-tab refresh: sessionStorage marker survives refresh; cleared on tab/browser close.
 */
export const isSameTabRefresh = () => {
  const hadMarker = !!sessionStorage.getItem(TAB_SESSION_MARKER);
  return hadMarker && isReloadNavigation();
};

const stashPendingCloseLogout = () => {
  try {
    const token = readPersistedToken();
    if (!token) return;
    // Defer logout until next bootstrap. If this unload was a refresh,
    // the session marker survives and we cancel the pending logout.
    localStorage.setItem(PENDING_TAB_CLOSE_LOGOUT_KEY, token);
  } catch {
    // ignore
  }
};

const cancelPendingCloseLogout = () => {
  try {
    localStorage.removeItem(PENDING_TAB_CLOSE_LOGOUT_KEY);
  } catch {
    // ignore
  }
};

const flushPendingCloseLogout = () => {
  try {
    const token = localStorage.getItem(PENDING_TAB_CLOSE_LOGOUT_KEY) || "";
    localStorage.removeItem(PENDING_TAB_CLOSE_LOGOUT_KEY);
    if (token) {
      callLogoutAPIKeepalive(token);
    }
  } catch {
    // ignore
  }
};

/**
 * Must run before Redux persist rehydrates (called from store.js).
 *
 * Rules:
 * - sessionStorage marker present → same tab (refresh / in-tab load) → KEEP auth
 * - marker missing → tab was closed or new tab → flush pending logout + clear orphan auth
 */
export const bootstrapTabSession = () => {
  try {
    migrateLegacyAuthStorage();
    sessionStorage.removeItem(EXPECT_RELOAD_KEY);
    sessionStorage.removeItem(TIME_ORIGIN_KEY);

    // Bill tab opened from Advance Payment → restore opener auth, never logout
    if (consumeBillTabAuthBridge()) {
      return;
    }

    const hadMarker = !!sessionStorage.getItem(TAB_SESSION_MARKER);

    if (hadMarker) {
      // Same tab still alive (refresh / soft reload). Do NOT logout.
      cancelPendingCloseLogout();
      sessionStorage.setItem(TAB_SESSION_MARKER, String(Date.now()));
      return;
    }

    // No marker → previous tab/browser closed, or brand-new tab.
    flushPendingCloseLogout();

    const token = readPersistedToken();
    if (token) {
      // Orphan session restore without marker — invalidate safely
      callLogoutAPIKeepalive(token);
      sessionStorage.removeItem(PERSIST_ROOT_KEY);
      sessionStorage.setItem(TAB_CLOSE_LOGOUT_FLAG, "1");
    }

    sessionStorage.setItem(TAB_SESSION_MARKER, String(Date.now()));
  } catch {
    // ignore
  }
};

export const consumeTabCloseLogoutFlag = () => {
  try {
    const shouldSignOut = sessionStorage.getItem(TAB_CLOSE_LOGOUT_FLAG) === "1";
    if (shouldSignOut) {
      sessionStorage.removeItem(TAB_CLOSE_LOGOUT_FLAG);
    }
    return shouldSignOut;
  } catch {
    return false;
  }
};

const markExpectReload = () => {
  try {
    sessionStorage.setItem(EXPECT_RELOAD_KEY, "1");
    // Cancel any prior pending close so a refresh never flushes logout
    cancelPendingCloseLogout();
  } catch {
    // ignore
  }
};

const isExpectingReload = () => {
  try {
    return sessionStorage.getItem(EXPECT_RELOAD_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * On unload: do NOT call logout API immediately (refresh race).
 * Stash token; bootstrap cancels it if this was only a refresh.
 */
export const installTabCloseLogoutHandlers = () => {
  if (typeof window === "undefined" || window.__eopsTabCloseHandlersInstalled) {
    return;
  }
  window.__eopsTabCloseHandlersInstalled = true;

  if (window.navigation && typeof window.navigation.addEventListener === "function") {
    window.navigation.addEventListener("navigate", (event) => {
      if (event?.navigationType === "reload") {
        markExpectReload();
      }
    });
  }

  window.addEventListener(
    "keydown",
    (event) => {
      if (event.key === "F5") {
        markExpectReload();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === "r") {
        markExpectReload();
      }
    },
    true,
  );

  const onUnloadMaybeClose = () => {
    if (isExpectingReload()) {
      cancelPendingCloseLogout();
      return;
    }
    stashPendingCloseLogout();
  };

  // Do not call logout API here — that was logging users out on refresh.
  window.addEventListener("beforeunload", onUnloadMaybeClose);

  window.addEventListener("pagehide", (event) => {
    if (event?.persisted) {
      cancelPendingCloseLogout();
      return;
    }
    if (isExpectingReload()) {
      cancelPendingCloseLogout();
      return;
    }
    stashPendingCloseLogout();
  });
};
